/**
 * Llamadas a la API.
 *
 * El token vive en `sessionStorage` y no en `localStorage`: una sesión abierta
 * en un ordenador compartido del taller no debe seguir abierta mañana porque
 * nadie cerró la pestaña.
 */
const CLAVE = 'mrp.token';
const CLAVE_SESION = 'mrp.sesion';

export function guardarToken(token: string): void {
  try {
    sessionStorage.setItem(CLAVE, token);
  } catch {
    // Navegador con el almacenamiento bloqueado: la sesión dura lo que dure
    // la página. Es peor que lo normal, pero funciona.
  }
}

export function leerToken(): string | null {
  try {
    return sessionStorage.getItem(CLAVE);
  } catch {
    return null;
  }
}

export function olvidarToken(): void {
  try {
    sessionStorage.removeItem(CLAVE);
    sessionStorage.removeItem(CLAVE_SESION);
  } catch {
    /* nada que hacer */
  }
}

/**
 * Guarda quién entró, solo para pintar su nombre al recargar la página.
 *
 * NO es una credencial ni se usa para decidir permisos: quien manda es el
 * token, que firma el servidor y comprueba en cada petición. Alterar esto a
 * mano cambia el nombre que se ve y nada más.
 */
export function guardarSesion(sesion: unknown): void {
  try {
    sessionStorage.setItem(CLAVE_SESION, JSON.stringify(sesion));
  } catch {
    /* la sesión durará lo que dure la página */
  }
}

export function leerSesion<T>(): T | null {
  try {
    const raw = sessionStorage.getItem(CLAVE_SESION);
    return raw === null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Qué se puede hacer a continuación. La API lo manda cuando lo hay. */
    readonly hint: string | null = null,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = leerToken();
  const res = await fetch(`/api${path}`, {
    ...options,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {}),
    },
  });

  const texto = await res.text();
  const cuerpo = texto === '' ? {} : (JSON.parse(texto) as Record<string, unknown>);

  if (!res.ok) {
    if (res.status === 401) olvidarToken();
    throw new ApiError(
      res.status,
      (cuerpo.error as string) ?? `Error ${res.status}`,
      (cuerpo.hint as string) ?? null,
    );
  }
  return cuerpo as T;
}

export const get = <T>(path: string): Promise<T> => api<T>(path);
export const post = <T>(path: string, body?: unknown): Promise<T> =>
  api<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
export const put = <T>(path: string, body?: unknown): Promise<T> =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) });
export const del = <T>(path: string): Promise<T> => api<T>(path, { method: 'DELETE' });
